import React, { useEffect, useState } from "react";
import { StateLanguage } from "../../languages/config/StateLanguage";
import { BrandDA } from "../../services/admin/brands.inventory.service";
import { Error } from "../../models/error.model";
import { Brand } from "../../models/brands.inventory.model";
import { GlobalSwal } from "../../styles/swal.style";
import { legendIconInvalid, legendIconValidInvalidRestart } from "../../utils/legend.data.util";
import { modalHide } from "../../utils/modal.util";
import { evaluateLegendRequiredIcon } from "../../scripts/validate.legend.script";
import { expressions } from "../../libraries/regular.expressions.library";
import { getError, getMessage } from "../../languages/translations/response";
import { errorWebHandler } from "../../scripts/error.web.handler.script";
import { removeUploadFile } from "../../scripts/file.load.script";
import { Company } from "../../models/company.model";
import AppLegend from "../../components/element/Legend";
import AppInputImage from "../../components/element/InputImage";

export interface AppModalBrandProps {
  companyForUser: Company | null;
  brandItem: Brand | null;
  onSuccess: (mode: "create" | "edit", createdBrand?: Brand) => void;
}

type ImageState = {
  value: { name: string; size: number; type: string; view: string; file: any };
  valid: { file: boolean; message: string };
};

let errorResponse: Error;

const AppModalBrand: React.FunctionComponent<AppModalBrandProps> = ({ companyForUser, brandItem, onSuccess }) => {
  const { lang } = StateLanguage();

  const [loadIndicator, setLoadIndicator] = useState("off");
  const [name, setName] = useState({ value: "", valid: false });
  const [logo, setLogo] = useState<ImageState>(removeUploadFile());
  const [isActive, setIsActive] = useState<boolean>(true);

  const isEditMode = brandItem !== null;

  // ─── Construcción del payload ────────────────────────────────────────────
  //
  // CREATE → siempre FormData con File (el campo logo es obligatorio)
  // EDIT   → si el usuario seleccionó un archivo nuevo  → FormData con File
  //          si el usuario NO cambió el logo             → JSON con string URL
  //
  const buildPayload = (): { request: Promise<any> } | null => {
    if (!companyForUser) return null;

    const hasNewFile = logo.value.file !== null;

    if (!isEditMode) {
      return {
        request: BrandDA.postBrand(companyForUser.id, { name: name.value, logo: logo.value.file }),
      };
    }

    // ── EDIT ─────────────────────────────────────────────────────────────────
    if (hasNewFile) {
      return {
        request: BrandDA.putBrand(companyForUser.id, brandItem!.id, { name: name.value, logo: logo.value.file, is_active: isActive }),
      };
    } else {
      const body = {
        name: name.value,
        logo: brandItem!.logo ?? "",
        is_active: isActive,
      };

      return {
        request: BrandDA.putBrand(companyForUser.id, brandItem!.id, body),
      };
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────
  const submitBrand = async () => {
    if (!companyForUser || !name.valid || !logo.valid.file) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {
        if (!name.value.length) legendIconInvalid("modal-brand-input-name", "modal-brand-legend-name-required");
      });
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    setLoadIndicator("on");

    await payload.request
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const mode = isEditMode ? "edit" : "create";
          const brandData = response.data?.data ?? response.data;
          onSuccess(mode, brandData as Brand);
          GlobalSwal.fire({
            title: getMessage(isEditMode ? "Successfully updated brand" : "Successfully created brand", lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: "success",
            showConfirmButton: false,
            timer: 2200,
          }).then(() => executeHideModal());
        } else {
          errorResponse = response.data;
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: "error",
            confirmButtonText: lang.labels.okGotIt,
          });
        }
      })
      .catch((error) => errorWebHandler(lang, error))
      .finally(() => setLoadIndicator("off"));
  };

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName({ ...name, value: event.target.value });
  };

  const handleChangeIsActive = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const validateName = () => {
    evaluateLegendRequiredIcon(
      expressions.text,
      name,
      setName,
      "modal-brand-input-name",
      "modal-brand-legend-name-valid",
      "modal-brand-legend-name-required",
    );
  };

  // ─── Modal helpers ────────────────────────────────────────────────────────
  const executeHideModal = () => {
    modalHide("modal-brand");
    setTimeout(() => restartModal(), 200);
  };

  const restartModal = () => {
    legendIconValidInvalidRestart("modal-brand-input-name", "modal-brand-legend-name-valid");
    legendIconValidInvalidRestart("modal-brand-input-name", "modal-brand-legend-name-required");
    setName({ value: "", valid: false });
    setLogo(removeUploadFile());
    setIsActive(true);
  };

  // ─── Efecto: cargar datos al abrir en modo edición ────────────────────────
  useEffect(() => {
    if (brandItem) {
      setName({ value: brandItem.name, valid: true });
      setLogo({
        value: {
          name: brandItem.logo ?? "",
          size: 0,
          type: "",
          view: brandItem.logo ?? "",
          file: null, // null indica que NO hay archivo nuevo seleccionado
        },
        valid: { file: true, message: "" },
      });
      setIsActive(brandItem.is_active);
    } else {
      restartModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandItem]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div id="modal-brand" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{isEditMode ? lang.labels.editBrand : lang.labels.addBrand}</h2>
          </div>

          <div className="modal-body">
            <div className="form">
              {/* ── Logo ──────────────────────────────────────────────── */}
              <div className="row mb-5">
                <label className="col-lg-3 col-form-label">{lang.labels.logo}</label>
                <div className="col-lg-9">
                  <AppInputImage image={logo} setImage={setLogo} />
                </div>
              </div>

              {/* ── Nombre ────────────────────────────────────────────── */}
              <div className={`row ${isEditMode ? "mb-2" : ""}`}>
                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                <div className="col-lg-9">
                  <input
                    id="modal-brand-input-name"
                    className="form-control"
                    type="text"
                    autoComplete="off"
                    name="name"
                    value={name.value}
                    onChange={handleChangeName}
                    onKeyUp={validateName}
                    onBlur={validateName}
                  />
                  <AppLegend
                    component={"modal-brand"}
                    attribute={{
                      check: name.valid,
                      name: "name",
                      index: null,
                      sub_index: null,
                    }}
                    container={{
                      valid: true,
                      required: true,
                      identical: false,
                      exists: false,
                    }}
                  />
                </div>
              </div>

              {/* ── Estado (solo edición) ──────────────────────────────── */}
              {isEditMode && (
                <div className="row">
                  <label className="col-lg-3 col-form-label">{lang.labels.status}</label>
                  <div className="col-lg-9 d-flex align-items-center">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="modal-brand-switch-is-active"
                        checked={isActive}
                        onChange={handleChangeIsActive}
                      />
                      <label className="form-check-label" htmlFor="modal-brand-switch-is-active">
                        {isActive ? (
                          <span className="badge badge-light-success">{lang.labels.active}</span>
                        ) : (
                          <span className="badge badge-light-secondary">{lang.labels.inactive}</span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>
              {lang.labels.discard}
            </button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitBrand}>
              <span className="indicator-label">{isEditMode ? lang.labels.saveChanges : lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppModalBrand;
